import React, { useState, useEffect, useContext } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { 
  createTemplate, 
  updateTemplate, 
  getTemplate,
  testWebhookTemplate 
} from "../../../services/TemplatesServices";
import UserContext from "../../../contexts/UserContext";
import NotificationsContext from "../../../contexts/NotificationsContext";
import { 
  IconArrowLeft, 
  IconDeviceFloppy, 
  IconEye, 
  IconSend,
  IconCheck,
  IconX,
  IconPlus,
  IconTrash,
} from "@tabler/icons-react";
import "./WebhookTemplateForm.css";

// Schema de validação
const webhookSchema = yup.object({
  name: yup.string().required("Nome é obrigatório"),
  webhook_url: yup.string()
    .url("URL inválida")
    .required("URL do webhook é obrigatória"),
  webhook_auth_token: yup.string()
    .max(255, "Token muito longo (máximo 255 caracteres)"),
  webhook_method: yup.string().required("Método HTTP é obrigatório")
});

const WebhookTemplateForm = ({ agentId: propAgentId = null }) => {
  const { user } = useContext(UserContext);
  const { setNotify } = useContext(NotificationsContext);
  const navigate = useNavigate();
  const { id, agentId: routeAgentId } = useParams();
  
  // Usar agentId da rota se disponível, senão usar prop
  const agentId = routeAgentId || propAgentId;
  
  // Detecta se está no contexto do AgentHub
  const isInsideAgentHub = Boolean(routeAgentId) || window.location.pathname.includes('/agent-hub/');
  
  // Função para navegar de volta
  const handleBackNavigation = () => {
    if (isInsideAgentHub && agentId) {
      navigate(`/agents/${agentId}/hub`);
    } else {
      navigate("/templates");
    }
  };
  const isEditing = !!id;

  const [isLoading, setIsLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [isTesting, setIsTesting] = useState(false);
  const [headers, setHeaders] = useState([{ key: '', value: '' }]);
  const [showAuthToken, setShowAuthToken] = useState(false);
  const [fieldMappings, setFieldMappings] = useState([{ key: '', value: '' }]);
  const [isTestingDirect, setIsTestingDirect] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue
  } = useForm({
    resolver: yupResolver(webhookSchema),
    defaultValues: {
      name: "",
      template_type: "webhook",
      action: "generic",
      webhook_url: "",
      webhook_auth_token: "",
      webhook_method: "POST"
    }
  });

  const watchedValues = watch();

  // Carrega dados do template para edição
  useEffect(() => {
    if (isEditing && id) {
      setIsLoading(true);
      getTemplate(id).then((data) => {
        setValue("name", data.name);
        setValue("action", data.action || "generic");
        setValue("template_type", "webhook");
        setValue("webhook_url", data.webhook_url || "");
        setValue("webhook_auth_token", data.webhook_auth_token || "");
        setValue("webhook_method", data.webhook_method || "POST");
        
        // Carrega headers se existirem
        if (data.webhook_headers && Object.keys(data.webhook_headers).length > 0) {
          const headersList = Object.entries(data.webhook_headers).map(([key, value]) => ({ key, value }));
          setHeaders(headersList.length > 0 ? headersList : [{ key: '', value: '' }]);
        }
        
        // Carrega mapeamento de campos se existir
        if (data.webhook_field_mapping && Object.keys(data.webhook_field_mapping).length > 0) {
          const mappingsList = Object.entries(data.webhook_field_mapping).map(([key, value]) => ({ key, value }));
          setFieldMappings(mappingsList.length > 0 ? mappingsList : [{ key: '', value: '' }]);
        }
        
        setIsLoading(false);
      }).catch(() => {
        setIsLoading(false);
        handleBackNavigation();
      });
    }
  }, [isEditing, id, setValue, navigate]);

  const addHeader = () => {
    setHeaders([...headers, { key: '', value: '' }]);
  };

  const removeHeader = (index) => {
    if (headers.length > 1) {
      setHeaders(headers.filter((_, i) => i !== index));
    }
  };

  const updateHeader = (index, field, value) => {
    const newHeaders = [...headers];
    newHeaders[index][field] = value;
    setHeaders(newHeaders);
  };

  const addFieldMapping = () => {
    setFieldMappings([...fieldMappings, { key: '', value: '' }]);
  };

  const removeFieldMapping = (index) => {
    if (fieldMappings.length > 1) {
      setFieldMappings(fieldMappings.filter((_, i) => i !== index));
    }
  };

  const updateFieldMapping = (index, field, value) => {
    const newMappings = [...fieldMappings];
    newMappings[index][field] = value;
    setFieldMappings(newMappings);
  };

  const onSubmit = async (data) => {
    setIsLoading(true);

    try {
      // Converte headers array para objeto, ignorando vazios
      const webhookHeaders = {};
      headers.forEach(header => {
        if (header.key.trim() && header.value.trim()) {
          webhookHeaders[header.key.trim()] = header.value.trim();
        }
      });

      // Converte field mappings array para objeto, ignorando vazios
      const webhookFieldMapping = {};
      fieldMappings.forEach(mapping => {
        if (mapping.key.trim() && mapping.value.trim()) {
          webhookFieldMapping[mapping.key.trim()] = mapping.value.trim();
        }
      });

      const templateData = {
        name: data.name,
        action: data.action,
        template_type: "webhook",
        webhook_url: data.webhook_url,
        webhook_headers: webhookHeaders,
        webhook_field_mapping: webhookFieldMapping,
        webhook_auth_token: data.webhook_auth_token || "",
        webhook_method: data.webhook_method,
        user: user.user_id,
        account_id: user.account_id,
        // Mensagem padrão para compatibilidade
        message: {
          text: `Webhook: ${data.name}`
        }
      };

      // Adicionar agent_id se fornecido (contexto do AgentHub)
      if (agentId) {
        templateData.agent = agentId;
      }

      if (isEditing) {
        await updateTemplate(id, templateData);
      } else {
        await createTemplate(templateData);
      }

      setNotify({
        title: "Sucesso!",
        message: `Template webhook ${isEditing ? 'atualizado' : 'criado'} com sucesso!`,
        type: "success",
        visible: true,
      });

      setTimeout(() => {
        handleBackNavigation();
      }, 1500);

    } catch (error) {
      console.error('Erro ao salvar template webhook:', error);
      setNotify({
        title: "Erro!",
        message: `Erro ao ${isEditing ? 'atualizar' : 'criar'} template webhook.`,
        type: "error",
        visible: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleTest = async () => {
    if (!id || !watchedValues.webhook_url) return;
    
    setIsTesting(true);
    
    try {
      await testWebhookTemplate(id);
      
      setTestResult({ 
        success: true, 
        message: "Webhook testado com sucesso!" 
      });
    } catch (error) {
      console.error('Erro no teste de webhook:', error);
      setTestResult({ 
        success: false, 
        message: "Falha ao testar webhook. Verifique a URL e configurações." 
      });
    } finally {
      setIsTesting(false);
    }
    
    setTimeout(() => setTestResult(null), 5000);
  };

  const handleDirectTest = async () => {
    if (!watchedValues.webhook_url) return;
    
    setIsTestingDirect(true);
    
    try {
      // Prepara headers da requisição
      const requestHeaders = {
        'Content-Type': 'application/json'
      };
      
      // Adiciona headers personalizados
      headers.forEach(header => {
        if (header.key.trim() && header.value.trim()) {
          requestHeaders[header.key.trim()] = header.value.trim();
        }
      });
      
      // Adiciona token de autenticação se existir
      if (watchedValues.webhook_auth_token) {
        requestHeaders['Authorization'] = `Bearer ${watchedValues.webhook_auth_token}`;
      }
      
      // Prepara dados do corpo da requisição com mapeamentos
      const mappedData = {};
      fieldMappings.forEach(mapping => {
        if (mapping.key.trim() && mapping.value.trim()) {
          mappedData[mapping.key.trim()] = mapping.value.trim();
        }
      });
      
      // Faz a requisição HTTP direta
      // Nota: Usando mode: 'no-cors' para evitar problemas de CORS
      // Isso significa que não teremos acesso ao response, mas a requisição será enviada
      const response = await fetch(watchedValues.webhook_url, {
        method: watchedValues.webhook_method || "POST",
        headers: requestHeaders,
        body: JSON.stringify(mappedData),
        mode: 'no-cors', // Evita preflight OPTIONS e problemas de CORS
        signal: AbortSignal.timeout(10000) // 10 segundos de timeout
      });
      
      // Com no-cors, response.ok sempre será false, então não podemos verificar
      // A requisição será enviada mesmo assim
      
      setTestResult({ 
        success: true, 
        message: "Webhook testado com sucesso! A requisição foi enviada." 
      });
    } catch (error) {
      console.error('Erro no teste direto de webhook:', error);
      let errorMessage = "Falha ao testar webhook.";
      
      if (error.name === 'AbortError' || error.name === 'TimeoutError') {
        errorMessage = "Timeout na requisição. Verifique a URL e tente novamente.";
      } else if (error.message.includes('HTTP')) {
        errorMessage = `Webhook respondeu com ${error.message}`;
      } else if (error.name === 'TypeError') {
        errorMessage = "Não foi possível conectar com o webhook. Verifique a URL.";
      }
      
      setTestResult({ 
        success: false, 
        message: errorMessage
      });
    } finally {
      setIsTestingDirect(false);
    }
    
    setTimeout(() => setTestResult(null), 8000);
  };

  const generatePreviewPayload = () => {
    const webhookHeaders = {};
    headers.forEach(header => {
      if (header.key.trim() && header.value.trim()) {
        webhookHeaders[header.key.trim()] = header.value.trim();
      }
    });

    const mappedData = {};
    fieldMappings.forEach(mapping => {
      if (mapping.key.trim() && mapping.value.trim()) {
        mappedData[mapping.key.trim()] = mapping.value.trim();
      }
    });

    return {
      method: watchedValues.webhook_method || 'POST',
      url: watchedValues.webhook_url || '',
      headers: {
        'Content-Type': 'application/json',
        ...webhookHeaders,
        ...(watchedValues.webhook_auth_token && {
          'Authorization': `Bearer ${watchedValues.webhook_auth_token}`
        })
      },
      body: {
        template_name: watchedValues.name || 'Webhook Template',
        action: watchedValues.action || 'generic',
        timestamp: new Date().toISOString(),
        data: mappedData,
        // Shortcodes que serão substituídos
        "nome_cliente": "{{nome_cliente}}",
        "telefone": "{{telefone}}",
        "data_agendamento": "{{data_agendamento}}"
      }
    };
  };

  if (isLoading) {
    return (
      <div className="webhook-template-form">
        <div className="webhook-template-form__loading">
          <div className="webhook-template-form__loading-spinner"></div>
          <p>Carregando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="webhook-template-form">
      <div className="webhook-template-form__header">
        <div className="webhook-template-form__header-left">
          <button className="webhook-template-form__back-button" onClick={handleBackNavigation}>
            <IconArrowLeft size={20} />
          </button>
          <div>
            <h1 className="webhook-template-form__title">
              {isEditing ? 'Editar' : 'Criar'} Template Webhook
            </h1>
            <p className="webhook-template-form__subtitle">
              Configure integrações com sistemas externos via webhook
            </p>
          </div>
        </div>
      </div>

      <div className="webhook-template-form__content">
        <div className="webhook-template-form__form-section">
          <form className="webhook-template-form__form" onSubmit={handleSubmit(onSubmit)}>
            <div className="webhook-template-form__form-group">
              <label className="webhook-template-form__label">Nome do Template</label>
              <input
                type="text"
                className={`webhook-template-form__input ${errors.name ? 'webhook-template-form__input--error' : ''}`}
                placeholder="Ex: Integração CRM - Novo Lead"
                {...register("name")}
              />
              {errors.name && <span className="webhook-template-form__error-text">{errors.name.message}</span>}
            </div>

            <div className="webhook-template-form__form-group">
              <label className="webhook-template-form__label">Tipo de Ação</label>
              <select
                className={`webhook-template-form__select ${errors.action ? 'webhook-template-form__select--error' : ''}`}
                {...register("action")}
              >
                <option value="generic">Genérico</option>
                <option value="confirmation_message">Confirmação</option>
                <option value="reminder_message">Lembrete</option>
                <option value="feedback">Feedback</option>
                <option value="integration">Integração</option>
              </select>
              {errors.action && <span className="webhook-template-form__error-text">{errors.action.message}</span>}
            </div>

            <div className="webhook-template-form__url-section">
              <div className="webhook-template-form__form-group">
                <label className="webhook-template-form__label">URL do Webhook *</label>
                <input
                  type="url"
                  className={`webhook-template-form__input ${errors.webhook_url ? 'webhook-template-form__input--error' : ''}`}
                  placeholder="https://api.exemplo.com/webhook"
                  {...register("webhook_url")}
                />
                {errors.webhook_url && <span className="webhook-template-form__error-text">{errors.webhook_url.message}</span>}
                <span className="webhook-template-form__help-text">
                  URL completa para onde os dados serão enviados
                </span>
              </div>

              <div className="webhook-template-form__form-group">
                <label className="webhook-template-form__label">Método HTTP</label>
                <select
                  className={`webhook-template-form__select ${errors.webhook_method ? 'webhook-template-form__select--error' : ''}`}
                  {...register("webhook_method")}
                >
                  <option value="POST">POST</option>
                  <option value="PUT">PUT</option>
                  <option value="PATCH">PATCH</option>
                </select>
                {errors.webhook_method && <span className="webhook-template-form__error-text">{errors.webhook_method.message}</span>}
              </div>
            </div>

            <div className="webhook-template-form__auth-section">
              <div className="webhook-template-form__form-group">
                <label className="webhook-template-form__label">Token de Autenticação</label>
                <div className="webhook-template-form__token-container">
                  <input
                    type={showAuthToken ? "text" : "password"}
                    className={`webhook-template-form__input ${errors.webhook_auth_token ? 'webhook-template-form__input--error' : ''}`}
                    placeholder="Bearer token ou API key"
                    {...register("webhook_auth_token")}
                  />
                  <button
                    type="button"
                    className="webhook-template-form__toggle-button"
                    onClick={() => setShowAuthToken(!showAuthToken)}
                  >
                    {showAuthToken ? <IconX size={16} /> : <IconEye size={16} />}
                  </button>
                </div>
                {errors.webhook_auth_token && <span className="webhook-template-form__error-text">{errors.webhook_auth_token.message}</span>}
                <span className="webhook-template-form__help-text">
                  Será enviado no cabeçalho Authorization: Bearer {'{token}'}
                </span>
              </div>
            </div>

            <div className="webhook-template-form__headers-section">
              <div className="webhook-template-form__headers-title">
                <h3>Headers Adicionais</h3>
                <button
                  type="button"
                  className="webhook-template-form__add-button"
                  onClick={addHeader}
                >
                  <IconPlus size={16} />
                  Adicionar Header
                </button>
              </div>
              
              <div className="webhook-template-form__headers-list">
                {headers.map((header, index) => (
                  <div key={index} className="webhook-template-form__header-item">
                    <input
                      type="text"
                      className="webhook-template-form__input webhook-template-form__header-key"
                      placeholder="Nome do header"
                      value={header.key}
                      onChange={(e) => updateHeader(index, 'key', e.target.value)}
                    />
                    <input
                      type="text"
                      className="webhook-template-form__input webhook-template-form__header-value"
                      placeholder="Valor do header"
                      value={header.value}
                      onChange={(e) => updateHeader(index, 'value', e.target.value)}
                    />
                    {headers.length > 1 && (
                      <button
                        type="button"
                        className="webhook-template-form__remove-button"
                        onClick={() => removeHeader(index)}
                      >
                        <IconTrash size={16} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              <span className="webhook-template-form__help-text">
                Headers personalizados enviados com a requisição (ex: X-API-Key, Custom-Header)
              </span>
            </div>

            <div className="webhook-template-form__headers-section">
              <div className="webhook-template-form__headers-title">
                <h3>Mapeamento de Campos</h3>
                <button
                  type="button"
                  className="webhook-template-form__add-button"
                  onClick={addFieldMapping}
                >
                  <IconPlus size={16} />
                  Adicionar Campo
                </button>
              </div>
              
              <div className="webhook-template-form__headers-list">
                {fieldMappings.map((mapping, index) => (
                  <div key={index} className="webhook-template-form__header-item">
                    <input
                      type="text"
                      className="webhook-template-form__input webhook-template-form__header-key"
                      placeholder="Nome do campo no webhook"
                      value={mapping.key}
                      onChange={(e) => updateFieldMapping(index, 'key', e.target.value)}
                    />
                    <input
                      type="text"
                      className="webhook-template-form__input webhook-template-form__header-value"
                      placeholder="Valor ou shortcode ({{nome_cliente}})"
                      value={mapping.value}
                      onChange={(e) => updateFieldMapping(index, 'value', e.target.value)}
                    />
                    {fieldMappings.length > 1 && (
                      <button
                        type="button"
                        className="webhook-template-form__remove-button"
                        onClick={() => removeFieldMapping(index)}
                      >
                        <IconTrash size={16} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              <span className="webhook-template-form__help-text">
                Configure quais campos serão enviados no webhook e seus valores (pode usar shortcodes como {`{{nome_cliente}}`})
              </span>
            </div>

            <div className="webhook-template-form__test-section">
              <div className="webhook-template-form__test-header">
                <IconSend size={18} />
                <h3>Teste de Webhook</h3>
              </div>
              
              <div className="webhook-template-form__test-container">
                <button 
                  type="button"
                  className="webhook-template-form__test-button"
                  onClick={handleDirectTest}
                  disabled={!watchedValues.webhook_url || isTestingDirect}
                  style={{ marginRight: '12px' }}
                >
                  {isTestingDirect ? "Testando..." : "Testar Webhook Agora"}
                </button>
                
                {isEditing && (
                  <button 
                    type="button"
                    className="webhook-template-form__secondary-button"
                    onClick={handleTest}
                    disabled={!watchedValues.webhook_url || isTesting}
                    style={{ padding: '12px 16px', fontSize: '14px' }}
                  >
                    {isTesting ? "Testando..." : "Testar Template Salvo"}
                  </button>
                )}
              </div>
              
              {testResult && (
                <div className={`webhook-template-form__test-result webhook-template-form__test-result--${testResult.success ? 'success' : 'error'}`}>
                  {testResult.success ? <IconCheck size={16} /> : <IconX size={16} />}
                  {testResult.message}
                </div>
              )}
              
              <span className="webhook-template-form__help-text">
                "Testar Webhook Agora" envia os dados atuais do formulário. {isEditing && '"Testar Template Salvo" usa o template já salvo.'}
              </span>
            </div>

            <div className="webhook-template-form__actions">
              <button 
                type="button" 
                className="webhook-template-form__secondary-button"
                onClick={handleBackNavigation}
              >
                Cancelar
              </button>
              <button 
                type="submit" 
                className="webhook-template-form__primary-button"
                disabled={isLoading}
              >
                {isLoading ? (
                  "Salvando..."
                ) : (
                  <>
                    <IconDeviceFloppy size={18} />
                    {isEditing ? 'Atualizar' : 'Criar'} Template
                  </>
                )}
              </button>
            </div>
          </form>
        </div>

        <div className="webhook-template-form__sidebar">
          <div className="webhook-template-form__preview-section">
            <div className="webhook-template-form__preview-header">
              <h3>Preview da Requisição</h3>
              <button
                type="button"
                className={`webhook-template-form__preview-toggle ${showPreview ? 'webhook-template-form__preview-toggle--active' : ''}`}
                onClick={() => setShowPreview(!showPreview)}
              >
                <IconEye size={16} />
                {showPreview ? 'Ocultar' : 'Mostrar'}
              </button>
            </div>
            
            {showPreview && watchedValues.webhook_url && (
              <div className="webhook-template-form__preview-content">
                <pre className="webhook-template-form__preview-code">
                  {JSON.stringify(generatePreviewPayload(), null, 2)}
                </pre>
              </div>
            )}
            
            {!watchedValues.webhook_url && showPreview && (
              <div className="webhook-template-form__preview-empty">
                Configure a URL do webhook para ver o preview
              </div>
            )}
          </div>

         
        </div>
      </div>
    </div>
  );
};

export default WebhookTemplateForm;